import React, { useState, useEffect } from "react";
import { useLocation, useParams } from "react-router-dom";
import "../styles/components/questions.css";
import { toast } from "react-toastify";

export const Questions = () => {
  const { topicId } = useParams();
  const location = useLocation();

  // Extract variables from location.state
  const state = location.state || {};
  const subject = state.subject || "Mathematics";
  const chapters = state.chapters || [];
  const topicName = state.topicName || topicId;

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedOption, setSelectedOption] = useState(null);
  const [activeTab, setActiveTab] = useState("hints");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const currentQuestion = questions[currentIndex];

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        setLoading(true);

        if (!subject || chapters.length === 0) {
          setError("Missing subject or chapter info.");
          return;
        }

        const allQuestions = [];

        for (const chapter of chapters) {
          const res = await fetch("/api/questions", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              subject,
              chapter: chapter.topicId,
              level: chapter.level,
            }),
          });

          const data = await res.json();
          if (data.questions) {
            allQuestions.push(...data.questions);
          } else {
            setError(`No questions found for ${chapter.topicName}.`);
          }
        }

        if (allQuestions.length > 0) {
          setQuestions(allQuestions);
        } else {
          setError("No questions found.");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch questions.");
      } finally {
        setLoading(false);
      }
    };

    fetchQuestions();
  }, [subject, chapters]);

  const handleOptionClick = (id) => setSelectedOption(id);

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedOption(null);
      setActiveTab("hints");
    } else {
      toast.info("You've completed all the questions!");
    }
  };

  if (loading)
    return (
      <div className="question-page">
        <h2>Loading questions...</h2>
      </div>
    );
  if (error)
    return (
      <div className="question-page">
        <h2>{error}</h2>
      </div>
    );
  if (!currentQuestion)
    return (
      <div className="question-page">
        <h2>No questions available for {topicName}</h2>
      </div>
    );

  return (
    <div className="question-page">
      <div className="question-header">
        <h3>{topicName} Challenge</h3>
        <p className="subtitle">
          Subject: {subject} | Level: {chapters[0]?.level || "Beginner"}
        </p>
        <div className="progress-timer">
          <span>
            Question {currentIndex + 1} of {questions.length}
          </span>
          <div className="progress-bar">
            <div
              className="progress"
              style={{
                width: `${((currentIndex + 1) / questions.length) * 100}%`,
              }}
            />
          </div>
          <span className="timer">⏱ 12:45 remaining</span>
        </div>
      </div>

      <div className="question-box">
        <p className="question">
          <b>{currentQuestion.question}</b>
        </p>
        <div className="options-grid">
          {currentQuestion.options.map((opt) => (
            <div
              key={opt.id}
              className={`option ${selectedOption === opt.id ? "selected" : ""}`}
              onClick={() => handleOptionClick(opt.id)}
            >
              <span>{opt.id}</span>
              <span>{opt.text}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="tabs-section">
        <div className="tabs">
          {["hints", "step", "solution"].map((tab) => (
            <button
              key={tab}
              className={activeTab === tab ? "active" : ""}
              onClick={() => setActiveTab(tab)}
            >
              {tab === "hints"
                ? "Hints"
                : tab === "step"
                  ? "Step by Step"
                  : "Solution"}
            </button>
          ))}
        </div>
        <div className="tab-content">
          {activeTab === "hints" && (
            <div className="hint-content">
              <div className="hint-box">
                <h4>Hint 1</h4>
                <p>This is a placeholder hint for: {topicName}</p>
              </div>
              <div className="hint-box">
                <h4>Hint 2</h4>
                <p>Use your knowledge of formulas and strategies!</p>
              </div>
            </div>
          )}
          {activeTab === "step" && (
            <div className="hint-content">
              Step-by-step explanation coming soon.
            </div>
          )}
          {activeTab === "solution" && (
            <div className="hint-content">Solution content coming soon.</div>
          )}
        </div>
      </div>

      <div className="question-actions">
        <button
          className="btn-secondary"
          disabled={currentIndex === 0}
          onClick={() => setCurrentIndex(currentIndex - 1)}
        >
          Previous Question
        </button>
        <button className="btn-primary" onClick={handleNext}>
          Submit & Continue
        </button>
      </div>
    </div>
  );
};

export default Questions;
